"use client";

import { ArrowLeft, Mail } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";

type ForgotPasswordShellProps = {
  children: ReactNode;
  title: string;
  description: string;
};

type FeaturePillProps = {
  icon: ReactNode;
  text: string;
};

const AUTO_PRIMARY = "#075F78";
const AUTO_PRIMARY_DARK = "#063D56";
const AUTO_PRIMARY_DEEP = "#042D40";

function FeaturePill({ icon, text }: FeaturePillProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 14px",
        borderRadius: "12px",
        backgroundColor: "rgba(255,255,255,0.07)",
        border: "1px solid rgba(255,255,255,0.10)",
        transition: "all 0.2s ease",
      }}>
      <div
        style={{
          flexShrink: 0,
          width: "30px",
          height: "30px",
          borderRadius: "8px",
          backgroundColor: "rgba(255,255,255,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.88)",
        }}>
        {icon}
      </div>
      <span style={{ color: "rgba(255,255,255,0.88)", fontSize: "14px", fontWeight: 600 }}>{text}</span>
    </div>
  );
}

export function ForgotPasswordShell({ children, title, description }: ForgotPasswordShellProps) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(145deg, ${AUTO_PRIMARY_DEEP} 0%, ${AUTO_PRIMARY} 48%, ${AUTO_PRIMARY_DARK} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}>
      <div
        style={{
          position: "absolute",
          top: "-160px",
          right: "-140px",
          width: "560px",
          height: "560px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 62%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-260px",
          left: "-140px",
          width: "720px",
          height: "720px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,0,0,0.07) 0%, transparent 64%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          pointerEvents: "none",
          maskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          width: "100%",
          maxWidth: "980px",
          borderRadius: "30px",
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.07), 0 20px 80px rgba(4,45,64,0.28), 0 32px 120px rgba(0,0,0,0.16)",
          minHeight: "640px",
        }}>
        <div
          className="hidden lg:flex"
          style={{
            flex: "0 0 42%",
            background: `linear-gradient(180deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
            borderRight: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "46px 40px",
            position: "relative",
          }}>
          <div
            style={{
              position: "absolute",
              top: "16%",
              left: "-18%",
              width: "320px",
              height: "320px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "-12%",
              bottom: "-10%",
              width: "240px",
              height: "240px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(4,45,64,0.30) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ marginBottom: "44px" }}>
              <Image
                src="/trafega-auto-logo.svg"
                alt="Trafega Pro"
                width={52}
                height={52}
                style={{ height: "52px", width: "52px", objectFit: "contain" }}
              />
            </div>

            <h1
              style={{
                color: "#ffffff",
                fontSize: "26px",
                fontWeight: 700,
                lineHeight: 1.28,
                marginBottom: "14px",
                letterSpacing: "-0.02em",
                maxWidth: "320px",
              }}>
              Recuperação simples e segura para manter sua operação rodando.
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.72)",
                fontSize: "15px",
                lineHeight: 1.6,
                marginBottom: "34px",
                maxWidth: "310px",
              }}>
              Solicite um novo link de acesso ou redefina sua senha e volte para os atendimentos da oficina.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <FeaturePill icon={<Mail size={15} />} text="Link enviado por e-mail" />
              <FeaturePill icon={<ArrowLeft size={15} />} text="Retorno rápido ao login" />
            </div>
          </div>

          <p
            style={{
              marginTop: "auto",
              paddingTop: "40px",
              fontSize: "12px",
              color: "rgba(255,255,255,0.38)",
            }}>
            trafegapro.com.br
          </p>
        </div>

        <div
          style={{
            flex: 1,
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "48px 44px",
            position: "relative",
          }}>
          <div className="mb-8 lg:hidden" style={{ textAlign: "center" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "14px",
                background: AUTO_PRIMARY,
                padding: "10px",
              }}>
              <Image
                src="/trafega-auto-logo.svg"
                alt="Trafega Pro"
                width={34}
                height={34}
                style={{ height: "34px", width: "34px", objectFit: "contain" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "28px" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "8px",
                letterSpacing: "-0.02em",
              }}>
              {title}
            </h2>
            <p style={{ fontSize: "15px", color: "#98a2b3", lineHeight: 1.6 }}>{description}</p>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
